import React, { useState, useEffect, useRef } from "react";
import { Spinner, Button, Alert, Form } from "react-bootstrap";
import { FaEdit, FaDownload } from "react-icons/fa";
import InwardLetter from "./InwardLetter";
import EditFormModal from "./EditFormModal";
import { generatePDF } from "./pdfGenerator";
import instance from "../../services/baseService";
import toast from "react-hot-toast";
import useCheckAuthorization from "../../hooks/checkAuthorization";
import { CRUD_PERMISSIONS } from "../../constants/user";
import Select from "react-select";
import { IoMdRefresh } from "react-icons/io";

const InwardTab = ({ clientId, reportId, type, port_code_id }) => {
    const [loading, setLoading] = useState(true);
    const [letterData, setLetterData] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [approving, setApproving] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const approvePermission = useCheckAuthorization(
        CRUD_PERMISSIONS.reconciliation.approve
    );
    const letterRef = useRef();
    const [prefaceOptions, setPrefaceOptions] = useState([]);
    const [selectedPreface, setSelectedPreface] = useState(null);
    const [loadingClients, setLoadingClients] = useState(false);

    // Reset all states to initial values
    const resetComponent = () => {
        setLetterData(null);
        setSelectedPreface(null);
        setPrefaceOptions([]);
        setError(null);
    };

    // Fetch letter data
    const fetchLetterData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await instance.post(
                `/api/v1/reconciliation/transfer-letter/get-data/${clientId}/${reportId}`, {
                port_code_id: port_code_id,
                type: "inward",
                latestData: 0
            }
            );
            const data = response.data.data;
            setLetterData(data);
            return data
        } catch (err) {
            setError("Failed to load letter data");
            console.error(err);
            return;
        } finally {
            setLoading(false);
        }
    };

    const getPrefaceDetails = async () => {
        setLoadingClients(true);
        try {
            const response = await instance.get(
                `/api/v1/reconciliation/preface/data?period_id=${reportId}&port_code_id=${port_code_id}`
            );

            const options = response.data?.data?.lists?.map(preface => ({
                value: preface.id,
                label: preface.name,
                data: preface
            })) || [];

            setPrefaceOptions(options);
            return options;
        } catch (error) {
            console.error('Error fetching preface details:', error);
            toast.error('Failed to load preface details');
        } finally {
            setLoadingClients(false);
        }
    };

    // Initialize component
    const initializeComponent = async () => {
        if (clientId && reportId) {
            await fetchLetterData();
            await getPrefaceDetails();
        }
    };

    useEffect(() => {
        initializeComponent();
    }, [clientId, reportId, type]);

    useEffect(() => {
        if (letterData?.generated === 1 && letterData?.preface_id && prefaceOptions.length > 0) {
            // Try to find preface in existing options
            let generatedPreface = prefaceOptions.find(
                option => option.value === letterData.preface_id
            );

            // If not found in options, create option from letterData
            if (!generatedPreface && letterData.preface_details) {
                generatedPreface = {
                    value: letterData.preface_id,
                    label: letterData.preface_details.name,
                    data: {
                        id: letterData.preface_id,
                        name: letterData.preface_details.name,
                        address: letterData.preface_details.address,
                        gstin: letterData.preface_details.gstin,
                        icegate_id: letterData.preface_details.icegate_id
                    }
                };

                // Add to prefaceOptions
                setPrefaceOptions(prevOptions => [...prevOptions, generatedPreface]);
            }

            setSelectedPreface(generatedPreface);
        }
    }, [letterData, prefaceOptions]);

    useEffect(() => {
        if (prefaceOptions.length > 0 && !selectedPreface && letterData?.generated !== 1) {
            setSelectedPreface(prefaceOptions[0]);
        }
    }, [prefaceOptions, selectedPreface, letterData?.generated]);

    const handlePrefaceSelect = (selectedOption) => {
        setSelectedPreface(selectedOption);
    };

    // Handle Generate PDF
    const handleGenerate = async () => {
        setGenerating(true);
        setError(null);
        try {
            // Generate PDF
            const pdfBlob = await generatePDF(letterRef.current, letterData);

            // Send to backend
            const formData = new FormData();
            formData.append("pdf", pdfBlob, `Transfer_Letter_${type}_${Date.now()}.pdf`);
            formData.append("type", type);
            formData.append("preface_id", selectedPreface?.value || "");
            formData.append("client_name", selectedPreface.data.name || "");
            formData.append("address", selectedPreface.data.address || "");
            formData.append("gstin", selectedPreface.data.gstin || "");
            formData.append("icegate_id", selectedPreface.data.icegate_id || "");
            formData.append("iec", letterData.iec || "");
            formData.append("date", letterData.date || "");
            formData.append("amount", letterData.amount || "");
            const response = await instance.post(
                `/api/v1/reconciliation/transfer-letter/generate/${clientId}/${reportId}/${port_code_id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.data.type === "success") {
                setLetterData({
                    ...letterData,
                    generated: 1,
                    file: response.data?.data?.file_url,
                    preface_id: selectedPreface?.value,
                    preface_details: selectedPreface?.data
                });
                toast.success(response.data.message);
            } else {
                setError(response.data.message || "Failed to generate PDF");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to generate PDF");
            console.error(err);
        }
        setGenerating(false);
    };

    // Handle Approve
    const handleApprove = async () => {
        setApproving(true);
        setError(null);
        try {
            const response = await instance.post(
                `/api/v1/reconciliation/transfer-letter/approve-reject/${clientId}/${reportId}/${port_code_id}`,
                {
                    type: type,
                    action: "approve"
                }
            );

            if (response.data.type === "success") {
                // Update local state
                setLetterData({
                    ...letterData,
                    approved: 1,
                    rejected: 0
                });
                toast.success(response.data.message);
            } else {
                setError(response.data.message || "Failed to approve letter");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to approve letter");
            console.error(err);
        }
        setApproving(false);
    };

    // Handle Reject
    const handleReject = async () => {
        if (!window.confirm("Are you sure you want to reject this letter?")) {
            return;
        }
        setRejecting(true);
        setError(null);
        try {
            const response = await instance.post(
                `/api/v1/reconciliation/transfer-letter/approve-reject/${clientId}/${reportId}/${port_code_id}`,
                {
                    type: type,
                    action: "reject"
                }
            );

            if (response.data.type === "success") {
                toast.success(response.data.message);
                // Reset and re-initialize (consistent with OutwardTab)
                resetComponent();
                await initializeComponent();
            } else {
                setError(response.data.message || "Failed to reject letter");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to reject letter");
            console.error(err);
        }
        setRejecting(false);
    };


    // Handle Download
    const handleDownload = () => {
        try {
            if (!letterData?.file) {
                throw new Error("No file URL found");
            }

            const fileUrl = letterData.file;
            const fileName = `${letterData.client_name}_${letterData.type}.pdf`;

            const link = document.createElement("a");
            link.href = fileUrl;
            link.download = fileName;
            link.target = "_blank"; // optional: open in new tab if browser blocks auto-download

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error(err);
            setError("Failed to download the file");
        } finally {
            setDownloading(false);
        }
    };


    const handleRegenerate = async () => {
        setRefreshing(true);
        setError(null);
        try {
            const response = await instance.post(
                `/api/v1/reconciliation/transfer-letter/refresh/${clientId}/${reportId}/${port_code_id}`, { type: "inward" },
            );
            if (response.data.type === "success") {
                toast.success(response.data.message);

                // Reset component and re-initialize
                resetComponent();
                await initializeComponent();
            } else {
                setError(response.data.message || "Failed to regenerate letter");
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Failed to regenerate letter");
            console.error(err);
        } finally {
            setRefreshing(false);
        }
    };



    if (loading || refreshing) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" />
            </div>
        );
    }

    const hasData = Boolean(letterData && Object.keys(letterData).length > 0);
    const isDataComplete = Boolean(
        letterData &&
        ["client_name", "date", "iec", "escrip_no", "amount", "port_code"].every(
            (key) => letterData[key] !== null && letterData[key] !== ""
        ) &&
        selectedPreface &&
        selectedPreface.data &&
        ["name", "address", "gstin", "icegate_id"].every(
            (key) =>
                selectedPreface.data[key] !== null &&
                selectedPreface.data[key] !== ""
        )
    );



    const showGenerateButton = isDataComplete && letterData.generated === 0;
    const showDownloadButton = isDataComplete && letterData.generated === 1;
    const showApproveReject = Boolean(hasData && letterData.generated && !letterData.approved && !letterData.rejected);
    // const showUploadButton = Boolean(hasData && letterData.approved && !letterData.ice_gate_status);
    const showRejectedLabel = Boolean(hasData && letterData.rejected);
    const showApprovedLabel = Boolean(hasData && letterData.approved);

    return (
        <div>
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            <div className="mb-4">
                {/* <Form.Label className="fw-bold">Select Preface</Form.Label> */}
                <Select
                    options={prefaceOptions}
                    value={selectedPreface}
                    onChange={handlePrefaceSelect}
                    isLoading={loadingClients}
                    isSearchable={false}
                    isDisabled={letterData?.generated === 1}
                    placeholder="Select Preface Data..."
                    noOptionsMessage={() => "Preface data not added in the server"}
                    className="react-select-container"
                    classNamePrefix="react-select"
                />
                {letterData?.generated === 1 && (
                    <small className="text-muted d-block mt-1">
                        Preface selection is locked after PDF generation
                    </small>
                )}
            </div>

            {!hasData ? (
                <div className="text-center py-5">
                    <p>No letter data available. Please update the information first.</p>
                </div>
            ) : (
                <>
                    {/* Action Buttons - Top */}
                    <div className="d-flex justify-content-between gap-2 mb-3">
                        {showDownloadButton && (
                            <Button
                                variant="link"
                                size="sm"
                                disabled={downloading || generating || refreshing}
                                onClick={() => handleDownload()}
                                className="text-decoration-none d-flex gap-2 align-items-center fw-bold"
                            >
                                <FaDownload /> Download PDF
                            </Button>
                        )}
                        {(showRejectedLabel || showApprovedLabel) && (
                            <Button
                                variant="link"
                                size="sm"
                                onClick={handleRegenerate}
                                disabled={downloading || generating || refreshing}
                                className="text-decoration-none d-flex gap-2 align-items-center fw-bold"
                            >
                                <IoMdRefresh /> Re-generate PDF
                            </Button>
                        )}
                    </div>
                    <div className="d-flex justify-content-center gap-3 mt-4">

                        {!isDataComplete && (
                            <div className="px-3 py-2 rounded-pill fw-bold text-yellow-700"
                                style={{ backgroundColor: "#fff3cd" }}>
                                Update the required fields to proceed.
                            </div>
                        )}
                    </div>
                    {/* Letter Content */}
                    <div ref={letterRef}>
                        <InwardLetter data={letterData} prefaceData={selectedPreface} />
                    </div>

                    {/* Action Buttons - Bottom */}
                    <div className="d-flex justify-content-center gap-3 mt-4">
                        {showGenerateButton && (
                            <Button
                                variant="primary"
                                className="fw-bold no-hover"
                                onClick={handleGenerate}
                                disabled={generating || refreshing}
                            >
                                {generating ? "Generating..." : "Generate PDF"}
                            </Button>
                        )}

                        {showApproveReject && (
                            <>
                                {approvePermission ? (
                                    <>
                                        <Button
                                            variant="success"
                                            className="fw-bold no-hover"
                                            onClick={handleApprove}
                                            disabled={approving || refreshing}
                                        >
                                            {approving ? "Approving..." : "Approve"}
                                        </Button>
                                        <Button
                                            variant="danger"
                                            className="fw-bold no-hover"
                                            onClick={handleReject}
                                            disabled={rejecting || refreshing}
                                        >
                                            {rejecting ? "Rejecting..." : "Reject"}
                                        </Button>
                                    </>
                                ) : (
                                    <div
                                        className="px-3 py-2 rounded-pill fw-bold text-yellow-700"
                                        style={{ backgroundColor: "#fff3cd" }}
                                    >
                                        Waiting for Approval
                                    </div>
                                )}
                            </>
                        )}

                        {showRejectedLabel && (
                            <div className="px-3 py-2 rounded-pill fw-bold text-white"
                                style={{ backgroundColor: "#dc3545" }}>
                                Rejected
                            </div>
                        )}
                        {showApprovedLabel && (
                            <div className="px-3 py-2 rounded-pill fw-bold text-white"
                                style={{ backgroundColor: "#28a745" }}>
                                Approved
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default InwardTab;